import styled, { css, DefaultTheme } from 'styled-components'

import { TableColumnAlign } from './types'

interface CellProps {
  $align?: TableColumnAlign
  $fixed?: boolean
  $scrolled?: boolean
  $width?: number
}

const fixedColumnShadow = (theme: DefaultTheme): ReturnType<typeof css> => css`
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 16px;
    transform: translateX(100%);
    box-shadow: inset 10px 0 8px -8px ${theme.color.mediumGrey};
    pointer-events: none;
  }
`

export const ScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  outline: none;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) =>
    `${theme.color.mediumGrey} ${theme.color.hoverWhite}`};

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.color.hoverWhite};
    border-radius: 7px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.color.mediumGrey};
    border-radius: 7px;
  }
`

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: ${({ theme }) => theme.color.white};
`

const cellWidth = ({ $width }: CellProps): string =>
  $width ? `min-width: ${$width}px; width: ${$width}px;` : 'min-width: 100px;'

export const Th = styled.th<CellProps>`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 16px;
  text-align: ${({ $align }) => $align ?? 'center'};
  font-weight: 600;
  font-size: 12px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
  background-color: ${({ theme }) => theme.color.hoverWhite};
  ${cellWidth}
  ${({ $fixed }) => $fixed && 'left: 0; z-index: 2;'}
  ${({ $fixed, $scrolled, theme }) =>
    $fixed && $scrolled && fixedColumnShadow(theme)}
`

export const Td = styled.td<CellProps>`
  height: 54px;
  padding: 8px 16px;
  text-align: ${({ $align }) => $align ?? 'center'};
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
  background-color: ${({ theme }) => theme.color.white};
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  ${cellWidth}
  ${({ $fixed }) => $fixed && 'position: sticky; left: 0; z-index: 1;'}
  ${({ $fixed, $scrolled, theme }) =>
    $fixed && $scrolled && fixedColumnShadow(theme)}
`

export const EmptyCell = styled.td`
  padding: 24px 16px;
  text-align: center;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.slateGrey};
  background-color: ${({ theme }) => theme.color.white};
`

export const CoverNameWrapper = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

export const Cover = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 24px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.lightGrey};
`

export const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const PlayOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  color: ${({ theme }) => theme.unchangingColor.white};

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`

export const Name = styled.span`
  font-weight: 600;
  font-size: 12px;
  line-height: 150%;
  text-align: left;
  color: ${({ theme }) => theme.color.darkGrey};
  word-break: break-word;
`
